/**
 * Cognito Helper Utilities
 * Centralized functions for fetching and managing Cognito user data
 */

import { getCurrentUser, fetchUserAttributes } from 'aws-amplify/auth';

/**
 * Get complete user information from Cognito
 * Fetches both authentication details and user attributes
 * 
 * @returns {Promise<Object>} Complete user information including attributes
 * @throws {Error} If user is not authenticated or attributes cannot be fetched
 */
export const getCognitoUserWithAttributes = async () => {
  try {
    // Get basic authentication info
    const currentUser = await getCurrentUser();
    
    // Fetch user attributes (includes given_name, family_name, email, phone, etc.)
    const attributes = await fetchUserAttributes();
    
    // Build comprehensive user object
    const completeUser = {
      // Core authentication fields
      userId: currentUser.userId,
      username: currentUser.username,
      sub: currentUser.userId, // Standard OIDC field
      
      // Standard attributes
      email: attributes.email || currentUser.signInDetails?.loginId || '',
      emailVerified: attributes.email_verified === 'true',
      givenName: attributes.given_name || '',
      familyName: attributes.family_name || '',
      fullName: buildFullName(attributes.given_name, attributes.family_name),
      phoneNumber: attributes.phone_number || '',
      phoneVerified: attributes.phone_number_verified === 'true',
      
      // Optional standard attributes
      birthdate: attributes.birthdate || '',
      gender: attributes.gender || '',
      address: attributes.address || '',
      locale: attributes.locale || 'en-AU',
      zoneinfo: attributes.zoneinfo || 'Australia/Sydney',
      
      // Custom attributes (if defined in Cognito User Pool)
      customAttributes: extractCustomAttributes(attributes),
      
      // Raw attributes for backward compatibility
      attributes: attributes
    };
    
    return completeUser;
    
  } catch (error) {
    const msg = error.message || '';
    const isAuthError = msg.includes('UserUnAuthenticatedException')
      || msg.includes('authenticated')
      || error.name === 'UserUnAuthenticatedException';
    if (!isAuthError) {
      console.error('Error fetching Cognito user:', msg);
    }
    throw error;
  }
};

/**
 * Build full name from given name and family name
 * Handles missing names gracefully
 * 
 * @param {string} givenName - User's given name
 * @param {string} familyName - User's family name
 * @returns {string} Full name or empty string
 */
const buildFullName = (givenName, familyName) => {
  const parts = [givenName, familyName].filter(Boolean);
  return parts.join(' ').trim();
};

/**
 * Extract custom attributes from Cognito attributes
 * Custom attributes have 'custom:' prefix
 * 
 * @param {Object} attributes - Raw Cognito attributes
 * @returns {Object} Custom attributes without prefix
 */
const extractCustomAttributes = (attributes) => {
  const customAttrs = {};
  
  Object.keys(attributes).forEach(key => {
    if (key.startsWith('custom:')) {
      const customKey = key.replace('custom:', '');
      customAttrs[customKey] = attributes[key];
    }
  });
  
  return customAttrs;
};

/**
 * Get basic user info (lightweight version)
 * Use this when you only need userId/username/email
 * 
 * @returns {Promise<Object>} Basic user information
 */
export const getBasicUserInfo = async () => {
  try {
    const currentUser = await getCurrentUser();
    
    return {
      userId: currentUser.userId,
      username: currentUser.username,
      email: currentUser.signInDetails?.loginId || ''
    };
    
  } catch (error) {
    console.error('❌ Error fetching basic user info:', error);
    throw new Error(`Failed to fetch user information: ${error.message}`);
  }
};

/**
 * Check if user has completed their profile
 * Based on presence of given_name and family_name
 * 
 * @returns {Promise<boolean>} True if profile has name fields
 */
export const hasCompletedCognitoProfile = async () => {
  try {
    const attributes = await fetchUserAttributes();
    return !!(attributes.given_name && attributes.family_name);
  } catch (error) {
    console.error('❌ Error checking profile completion:', error);
    return false;
  }
};

/**
 * Format user display name for UI
 * Priority: Full Name > Given Name > Username > Email
 * 
 * @param {Object} user - User object from getCognitoUserWithAttributes
 * @returns {string} Display name
 */
export const getUserDisplayName = (user) => {
  if (!user) return 'User';
  
  if (user.fullName) return user.fullName;
  if (user.givenName) return user.givenName;
  if (user.username) return user.username;
  if (user.email) return user.email.split('@')[0];
  
  return 'User';
};

/**
 * Format user initials for avatar
 * 
 * @param {Object} user - User object from getCognitoUserWithAttributes
 * @returns {string} User initials (e.g., "JS" for John Smith)
 */
export const getUserInitials = (user) => {
  if (!user) return '??';
  
  if (user.givenName && user.familyName) {
    return `${user.givenName[0]}${user.familyName[0]}`.toUpperCase();
  }
  
  if (user.givenName) {
    return user.givenName.substring(0, 2).toUpperCase();
  }
  
  if (user.username) {
    return user.username.substring(0, 2).toUpperCase();
  }
  
  if (user.email) {
    return user.email.substring(0, 2).toUpperCase();
  }
  
  return '??';
};

export default {
  getCognitoUserWithAttributes,
  getBasicUserInfo,
  hasCompletedCognitoProfile,
  getUserDisplayName,
  getUserInitials
};

















